import { HttpClient, HttpParams } from '@angular/common/http';
// Angular modules.
import { Injectable } from '@angular/core';
// Zenklub interfaces.
import {
	IAvailability,
	IAvailabilityApi,
	IAvailabilityPeriod,
	IBusySlot,
	INewAvailabilityApi,
	IUpdateAvailabilityApi,
	IWeekdaySlot,
	IWeekdaySlotItem,
	RecurrencyRemoveRange,
} from 'app/interfaces/availability.interface';
import { Observable, from, throwError } from 'rxjs';
import { catchError, concatMap, map, tap } from 'rxjs/operators';

import { CacheService } from './cache.service';
// Zenklub services.
import { DateTimeService } from './datetime.service';
import { UserService } from './user.service';

@Injectable()
export class AvailabilitiesService {
	// Security data.
	public userId: string;

	collectionName = 'busy-slot';

	// Constructor.
	constructor(
		private dateTime: DateTimeService,
		private http: HttpClient,
		private userService: UserService,
		private cacheService: CacheService
	) {
		// Set token if saved in local storage
		const currentUser = this.userService.getUserLocal();
		this.userId = currentUser && currentUser.id;
	}

	public getNextAvailableDate(params) {
		return this.http.get(`professionals/availabilities/nextAvailabilities`, {
			params,
		});
	}

	// Get all availabilities.
	public getAll(): Observable<IAvailabilityApi[]> {
		return this.http
			.get<IAvailabilityApi[]>('professionals/availabilities')
			.pipe(
				tap((results) => {
					// sort by date
					results.forEach((av) => {
						av.periods.sort((a, b) => {
							const aux = a.dayOfWeek - b.dayOfWeek;
							if (aux === 0) {
								return (
									this.buildTimeSpan(a.startTime) -
									this.buildTimeSpan(b.startTime)
								);
							}
							return aux;
						});
					});
				})
			);
	}

	private buildTimeSpan(value: string): number {
		const timespan: string[] = value.split(':');
		return Number(timespan[0]);
	}

	// Remove an availability.
	public remove(id: IAvailabilityApi['id']): Observable<unknown> {
		return this.http.delete(`professionals/availabilities/${id}`);
	}

	// Save new avaliability data.
	public save(data: IAvailability): Observable<IAvailabilityApi> {
		const availabilities: INewAvailabilityApi = this.buildFinalObject(data);
		return this.http.post<IAvailabilityApi>(
			'professionals/availabilities',
			availabilities
		);
	}

	// Edit an availability.
	public edit(data: IAvailability): Observable<IAvailabilityApi> {
		const availabilities = this.buildFinalObject(
			data,
			true
		) as IUpdateAvailabilityApi;
		return this.http.put<IAvailabilityApi>(
			`professionals/availabilities/${availabilities.id}`,
			availabilities
		);
	}

	public addBusySlot(data: IBusySlot): Observable<IBusySlot> {
		const body = {
			startDate: data.startDate.toISOString(),
			endDate: data.endDate.toISOString(),
			description: data?.description,
			recurrencyType: data?.recurrencyType,
		};
		// Request to update item.
		return new Observable<IBusySlot>((observer) => {
			this.http.post<IBusySlot>('professionals/v2/busyslots', body).subscribe(
				async (busySlot) => {
					await this.updateLocalBusySlot(busySlot);
					return observer.next(busySlot);
				},
				(error) => observer.error(error)
			);
		});
	}

	public removeBusySlot(
		id: IBusySlot['id'],
		recurrencyId?: IBusySlot['recurrencyId'],
		recurrencyRemoveRange?: RecurrencyRemoveRange
	): Observable<IBusySlot> {
		// Request to delete item.
		const body = {
			recurrencyId,
			recurrencyRemoveRange: recurrencyId
				? recurrencyRemoveRange
				: RecurrencyRemoveRange.ONLY,
		};

		return this.http
			.delete<IBusySlot>(`professionals/v2/busyslots/${id}`, { body })
			.pipe(
				concatMap((busySlot) => {
					return from(this.cacheService.remove(this.collectionName, id)).pipe(
						map(() => busySlot)
					);
				}),
				catchError((error) => {
					return throwError(error);
				})
			);
	}

	// Get events from user.
	public getBusySlotsFromServer(
		minDate?: Date,
		maxDate?: Date
	): Observable<IBusySlot[]> {
		// Parameters.
		let params = new HttpParams();
		params = params.set('limit', '1000');
		if (minDate) params = params.set('minStartDate', minDate.toISOString());
		if (maxDate) params = params.set('maxStartDate', maxDate.toISOString());
		return this.http.get<IBusySlot[]>('professionals/busyslots', { params });
	}

	// Get user's events.
	public async getBusySlots(
		minDate?: Date,
		maxDate?: Date
	): Promise<IBusySlot[]> {
		if (minDate) minDate.setHours(0, 0, 0, 0);
		if (maxDate) maxDate.setHours(23, 59, 59);
		const busySlots = await this.getBusySlotsFromServer(
			minDate || undefined,
			maxDate || undefined
		).toPromise();

		return busySlots.map((busySlot: any) => {
			const newBusySlot: IBusySlot = {
				id: busySlot.id,
				professionalId: busySlot.professionalId,
				description: busySlot?.description,
				endDate: new Date(busySlot.endDate),
				startDate: new Date(busySlot.startDate),
				isoStartDate: busySlot.startDate,
				isoEndDate: busySlot.endDate,
			};
			return newBusySlot;
		});
	}

	// Get events for a specific day.
	async getBusySlotsForDay(today: Date): Promise<IBusySlot[]> {
		const maxDate: Date = new Date(today);
		today.setHours(0, 0, 0, 0);
		maxDate.setHours(23, 59, 59);
		const busySlots = await this.getBusySlotsFromServer(
			today || undefined,
			maxDate || undefined
		).toPromise();
		return busySlots.map((busySlot: any) => {
			const newBusySlot: IBusySlot = {
				id: busySlot.id,
				professionalId: busySlot.professionalId,
				description: busySlot?.description,
				endDate: new Date(busySlot.endDate),
				startDate: new Date(busySlot.startDate),
			};
			return newBusySlot;
		});
	}

	public async getBusySlot(id: IBusySlot['id']): Promise<IBusySlot> {
		const busySlot = await this.cacheService.getByKey(this.collectionName, id);
		const newBusySlot: IBusySlot = {
			id: busySlot.id,
			professionalId: busySlot.professionalId,
			description: busySlot?.description,
			endDate: new Date(busySlot.endDate),
			startDate: new Date(busySlot.startDate),
			recurrencyId: busySlot.recurrencyId,
			recurrencyType: busySlot.recurrencyType,
		};
		return newBusySlot;
	}

	public updateLocalBusySlot(item: IBusySlot): Promise<IBusySlot> {
		const busySlot = {
			id: item.id,
			professionalId: item.professionalId,
			startDate: new Date(item.startDate),
			endDate: new Date(item.endDate),
			description: item?.description,
			date: new Date(item.startDate).getTime(),
			recurrencyId: item.recurrencyId,
			recurrencyType: item.recurrencyType,
		};

		return this.cacheService.update(this.collectionName, busySlot);
	}

	public updateLocalBusySlots() {
		this.getBusySlotsFromServer().subscribe(async (result) => {
			if (!result) {
				return;
			}

			const addPromises = result.map((item) => this.updateLocalBusySlot(item));
			await Promise.all(addPromises);
		});
	}

	// Build final availabilities object.
	public buildFinalObject(
		data: IAvailability,
		update = false
	): INewAvailabilityApi | IUpdateAvailabilityApi {
		// Local weekday slots.
		const wdSlots: IAvailabilityPeriod[] = [];

		// Copy from original to new.
		data.weekdaySlots.forEach((item) => {
			item.items.forEach((slot) => {
				// Build timeslots.
				const ts: IAvailabilityPeriod = {
					dayOfWeek: item.dayOfWeek,
					startTime: slot.startString,
					endTime: slot.endString,
				};
				// Add to final object.
				wdSlots.push(ts);
			});
		});

		if (update) {
			return {
				id: data.id,
				startDate: data.startDate,
				endDate: data.endDate,
				periods: wdSlots,
				timezone: data.timezone,
			} as IUpdateAvailabilityApi;
		}
		// Build new object.
		return {
			startDate: data.startDate,
			endDate: data.endDate,
			periods: wdSlots,
			timezone: data.timezone,
		} as INewAvailabilityApi;
	}

	// Build a IAvaliabity from server data.
	public buildFromServerData(preSlot: IAvailabilityApi): IAvailability {
		const wdSlots: IWeekdaySlot[] = [];
		// Step over time slots.
		preSlot.periods.forEach((timeSlot) => {
			// Get hours and minutes.
			const sH: number = parseInt(timeSlot.startTime.split(':')[0], 10);
			const sM: number = parseInt(timeSlot.startTime.split(':')[1], 10);
			const eH: number = parseInt(timeSlot.endTime.split(':')[0], 10);
			const eM: number = parseInt(timeSlot.endTime.split(':')[1], 10);

			// Create a new Slot Item.
			const t: IWeekdaySlotItem = {
				startHours: sH,
				startMinutes: sM,
				endHours: eH,
				endMinutes: eM,
				startString: this.dateTime.getHourAndMinutes(timeSlot.startTime),
				endString: this.dateTime.getHourAndMinutes(timeSlot.endTime),
				key:
					'slo.' +
					Math.floor(Math.random() * 100).toString() +
					'.' +
					Date.now().toFixed(),
			};

			// Value 'true' means that weekday already exists on object.
			let dayExists = false;

			wdSlots.forEach((s) => {
				if (s.dayOfWeek === timeSlot.dayOfWeek) {
					s.items.push(t);
					dayExists = true;
				}
			});

			if (!dayExists) {
				const ws: IWeekdaySlot = this.createWeekdaySlot(timeSlot.dayOfWeek, [
					t,
				]);
				wdSlots.push(ws);
			}
		});

		const finalObj: IAvailability = {
			id: preSlot.id,
			startDate: this.dateTime.getShortDate(
				preSlot.startDate,
				preSlot.timezone
			),
			endDate: this.dateTime.getShortDate(preSlot.endDate, preSlot.timezone),
			weekdaySlots: wdSlots,
			timezone: preSlot.timezone,
			key:
				'ava.' +
				Math.floor(Math.random() * 1000).toString() +
				'.' +
				Date.now().toFixed(),
		};
		return finalObj;
	}

	// Create new Weekday slot object.
	public createWeekdaySlot(
		dayOfWeek = 0,
		items: IWeekdaySlotItem[]
	): IWeekdaySlot {
		const tSlot: IWeekdaySlot = {
			dayOfWeek: dayOfWeek,
			items: items,
			key:
				'wk.' +
				Math.floor(Math.random() * 100).toString() +
				'.' +
				Date.now().toFixed(),
		};
		return tSlot;
	}

	// Create new SlotItem object.
	public createSlotItem(
		startHours = 8,
		startMinutes = 0,
		endHours = 9,
		endMinutes = 0
	): IWeekdaySlotItem {
		const wdsItem: IWeekdaySlotItem = {
			startHours: startHours,
			startMinutes: startMinutes,
			endHours: endHours,
			endMinutes: endMinutes,
			startString: this.dateTime.getHourString(startHours, startMinutes),
			endString: this.dateTime.getHourString(endHours, endMinutes),
			key:
				'slo.' +
				Math.floor(Math.random() * 100).toString() +
				'.' +
				Date.now().toFixed(),
		};
		return wdsItem;
	}

	public updateBusySlot(
		data: Pick<
			IBusySlot,
			'startDate' | 'endDate' | 'description' | 'id' | 'recurrencyId'
		> & {
			recurrencyEditRange: RecurrencyRemoveRange;
		}
	): Observable<IBusySlot> {
		const body = { ...data, id: undefined };
		// Request to update item.
		return new Observable<IBusySlot>((observer) => {
			this.http
				.put<IBusySlot>(`professionals/v2/busyslots/${data.id}`, body)
				.subscribe(
					async (busySlot) => {
						await this.updateLocalBusySlot(busySlot);
						return observer.next(busySlot);
					},
					(error) => observer.error(error)
				);
		});
	}
}
